import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { firstValueFrom } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AuthorizedApiService } from '@core';
import { BreadcrumbComponent } from '@shared/components/breadcrumb/breadcrumb.component';
import { LoadingStateComponent } from '@shared/components/loading-state/loading-state.component';
import { extractApiErrorMessage } from '@shared/utils/api-error.util';
import { AuditChange, SystemLogRecord } from './system-log.interface';
import { UserConfig } from '../../config/users/user.interface';

@Component({
  selector: 'app-system-log-detail',
  standalone: true,
  imports: [CommonModule, BreadcrumbComponent, LoadingStateComponent, MatButtonModule, MatIconModule, TranslateModule],
  templateUrl: './system-log-detail.component.html',
  styleUrl: './system-log-detail.component.scss',
})
export class SystemLogDetailComponent implements OnInit {
  log: SystemLogRecord | null = null;
  isLoading = true;

  private readonly api = inject(AuthorizedApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);

  ngOnInit(): void {
    const logId = this.route.snapshot.paramMap.get('logId');
    if (!logId) {
      void this.goBack();
      return;
    }
    void this.load(logId);
  }

  get actionLabel(): string {
    const action = (this.log?.action || '').trim().toLowerCase();
    if (!action) return '-';
    const key = `systemLogsCatalog.actions.${action}`;
    const translated = this.t(key);
    return translated === key ? action : translated;
  }

  get modelLabel(): string {
    const model = (this.log?.model || this.log?.moduleKey || '').trim();
    if (!model) return '-';
    const key = `systemLogsCatalog.models.${model}`;
    const translated = this.t(key);
    if (translated !== key) return translated;
    return model.replace(/([a-z\d])([A-Z])/g, '$1 $2').replace(/[-_]+/g, ' ');
  }

  get actorLabel(): string {
    if (this.log?.actorName) return this.log.actorName;
    const via = (this.log?.actor?.via || '').trim().toLowerCase();
    if (via === 'firebase') {
      return this.t('systemLogsCatalog.unknownUser');
    }
    if (via === 'service') {
      return this.t('systemLogsCatalog.automaticSystem');
    }
    return this.log?.actor?.uid || '-';
  }

  get isSnapshotAction(): boolean {
    const action = (this.log?.action || '').trim().toLowerCase();
    const data = this.log?.data;
    return ['add', 'create', 'remove', 'delete'].includes(action)
      && !!data
      && !Array.isArray(data)
      && typeof data === 'object'
      && !('kind' in data);
  }

  get isCreateAction(): boolean {
    return ['add', 'create'].includes((this.log?.action || '').trim().toLowerCase());
  }

  get snapshotJson(): string {
    return this.isSnapshotAction ? JSON.stringify(this.log?.data, null, 2) : '';
  }

  get snapshotFieldCount(): number {
    const data = this.log?.data;
    return this.isSnapshotAction && data && !Array.isArray(data) ? Object.keys(data).length : 0;
  }

  async goBack(): Promise<void> {
    await this.router.navigate(['/attendance/system-logs'], { queryParamsHandling: 'preserve' });
  }

  formatDate(value?: string | null): string {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return new Intl.DateTimeFormat(this.translate.currentLang?.startsWith('en') ? 'en-US' : 'es-MX', {
      dateStyle: 'long', timeStyle: 'medium',
    }).format(date);
  }

  fieldPath(change: AuditChange): string {
    return change.path?.join('.') || this.t('systemLogsCatalog.detail.rootRecord');
  }

  changeKind(change: AuditChange): string {
    const map: Record<string, string> = { N: 'created', D: 'deleted', E: 'edited', A: 'array' };
    const name = map[change.kind] || change.kind;
    const key = `systemLogsCatalog.changeKinds.${name}`;
    const result = this.t(key);
    return result === key ? name : result;
  }

  displayValue(value: unknown): string {
    if (value === undefined) return this.t('systemLogsCatalog.detail.notAvailable');
    if (value === null) return 'null';
    if (typeof value === 'string') return value || '""';
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    return String(value);
  }

  async copySnapshot(): Promise<void> {
    if (!this.snapshotJson) return;
    try {
      await navigator.clipboard.writeText(this.snapshotJson);
      this.snackBar.open(this.t('systemLogsCatalog.detail.jsonCopied'), '', {
        duration: 1800,
        verticalPosition: 'bottom',
        horizontalPosition: 'center',
        panelClass: 'snackbar-success',
      });
    } catch (error) {
      console.warn('Could not copy audit JSON', error);
    }
  }

  hasTechnicalData(): boolean {
    return !!this.log && (!!this.log.metadata || !!this.log.ref || !!this.log.correlationId || !!this.log.idempotencyKey);
  }

  get changes(): AuditChange[] {
    const data = this.log?.data;
    if (Array.isArray(data)) {
      return data.flatMap((change) => this.normalizeDeepDiff(change));
    }
    if (!data || typeof data !== 'object') {
      return [];
    }

    if ('kind' in data && typeof data['kind'] === 'string') {
      return this.normalizeDeepDiff(data as unknown as AuditChange);
    }

    const objectValues = Object.values(data);
    if (objectValues.length && objectValues.every((value) => this.isDeepDiffChange(value))) {
      return objectValues.flatMap((value) => this.normalizeDeepDiff(value as AuditChange));
    }

    const action = (this.log?.action || '').toLowerCase();
    const isCreate = action === 'create' || action === 'add';
    const isDelete = action === 'delete' || action === 'remove';
    return Object.entries(data).map(([field, value]) => ({
      kind: isCreate ? 'N' : isDelete ? 'D' : 'E',
      path: [field],
      ...(isDelete ? { lhs: value } : { rhs: value }),
    }));
  }

  private normalizeDeepDiff(change: AuditChange, parentPath: Array<string | number> = []): AuditChange[] {
    const path = [...parentPath, ...(change.path || [])];

    if (change.kind === 'A' && typeof change.index === 'number' && change.item) {
      return this.normalizeDeepDiff(change.item, [...path, change.index]);
    }

    if (
      (change.kind === 'E' || change.kind === 'N')
      && change.lhs === undefined
      && this.isDeepDiffChange(change.rhs)
    ) {
      return this.normalizeDeepDiff(change.rhs as AuditChange, parentPath);
    }

    return [{ ...change, path }];
  }

  private isDeepDiffChange(value: unknown): value is AuditChange {
    return !!value
      && typeof value === 'object'
      && 'kind' in value
      && typeof (value as { kind?: unknown }).kind === 'string';
  }

  private async load(logId: string): Promise<void> {
    this.isLoading = true;
    try {
      this.log = await firstValueFrom(this.api.get<SystemLogRecord>(`logs/${logId}`));
      await this.resolveFirebaseActor();
    } catch (error) {
      console.error('Error loading system audit log detail', error);
      this.snackBar.open(extractApiErrorMessage(error, this.t('systemLogsCatalog.notifications.loadDetailError')), '', {
        duration: 2600, verticalPosition: 'bottom', horizontalPosition: 'center', panelClass: 'snackbar-danger',
      });
      await this.goBack();
    } finally {
      this.isLoading = false;
    }
  }

  private async resolveFirebaseActor(): Promise<void> {
    const actor = this.log?.actor;
    if (!this.log || !actor?.uid || (actor.via || '').trim().toLowerCase() !== 'firebase') {
      return;
    }

    try {
      const users = await firstValueFrom(this.api.get<UserConfig[]>('users'));
      const user = (users || []).find((item) => item.uid === actor.uid);
      this.log.actorName = user?.displayName || user?.email || '';
    } catch (error) {
      console.warn('Could not resolve Firebase audit actor', error);
    }
  }

  private t(key: string): string {
    return this.translate.instant(key);
  }
}
